import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Music, Calendar, Ticket, CheckCircle2, TrendingUp, ArrowRight, Trophy } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { StageStatusBadge, formatDatePt } from '@/components/Badges';

export default function AdminDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/admin/dashboard').then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="text-muted-foreground">Carregando…</div>;

  const kpis = [
    { icon: Music, label: 'Bandas ativas', value: data.bands_total, testId: 'kpi-bands' },
    { icon: Calendar, label: 'Etapas totais', value: data.stages_total, testId: 'kpi-stages' },
    { icon: TrendingUp, label: 'Votações abertas', value: data.stages_open, testId: 'kpi-open' },
    { icon: Trophy, label: 'Etapas finalizadas', value: data.stages_finished, testId: 'kpi-finished' },
    { icon: Ticket, label: 'Comandas geradas', value: data.commands_total, testId: 'kpi-commands' },
    { icon: CheckCircle2, label: 'Comandas usadas', value: data.commands_used, testId: 'kpi-commands-used' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <div className="font-display text-4xl text-white">DASHBOARD</div>
        <p className="text-muted-foreground">Visão geral do campeonato.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {kpis.map((k) => (
          <Card key={k.label} className="border border-border bg-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <k.icon className="w-4 h-4 text-[var(--g66-amber)]" />
                <div className="text-xs text-muted-foreground">{k.label}</div>
              </div>
              <div className="font-display text-3xl text-white mt-1" data-testid={k.testId}>{k.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick actions */}
      <Card className="border border-border bg-card">
        <CardContent className="p-5">
          <div className="font-display text-2xl text-white mb-3">AÇÕES RÁPIDAS</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Link to="/admin/bandas"><Button variant="secondary" className="w-full">Cadastrar banda</Button></Link>
            <Link to="/admin/etapas"><Button variant="secondary" className="w-full">Criar etapa</Button></Link>
            <Link to="/admin/etapas"><Button variant="secondary" className="w-full">Gerar comandas</Button></Link>
            <Link to="/admin/configuracoes"><Button variant="secondary" className="w-full">Configurações</Button></Link>
          </div>
        </CardContent>
      </Card>

      {/* Next stage */}
      {data.next_stage && (
        <Card className="border border-border bg-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display text-2xl text-white">PRÓXIMA ETAPA</div>
              <StageStatusBadge status={data.next_stage.status} />
            </div>
            <div className="text-sm text-muted-foreground">{formatDatePt(data.next_stage.date)}</div>
            <div className="font-semibold text-white text-lg">{data.next_stage.title}</div>
            <div className="text-sm text-muted-foreground mt-1">
              {data.next_stage.band_a?.name || 'A definir'} <span className="text-[var(--g66-red)]">VS</span> {data.next_stage.band_b?.name || 'A definir'}
            </div>
            <div className="mt-4">
              <Link to={`/admin/etapas/${data.next_stage.id}`}>
                <Button className="bg-[var(--g66-amber)] text-black hover:bg-[var(--g66-amber-2)]">
                  Gerenciar etapa <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent finished */}
      {data.recent_finished?.length > 0 && (
        <Card className="border border-border bg-card">
          <CardContent className="p-5">
            <div className="font-display text-2xl text-white mb-3">ÚLTIMAS ETAPAS FINALIZADAS</div>
            <div className="space-y-2">
              {data.recent_finished.map((s) => (
                <div key={s.id} className="flex items-center justify-between p-3 rounded-lg bg-[var(--g66-surface)] border border-border">
                  <div className="min-w-0">
                    <div className="text-xs text-muted-foreground">{formatDatePt(s.date)}</div>
                    <div className="font-semibold text-white truncate">{s.title}</div>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Trophy className="w-4 h-4 text-[var(--g66-amber)]" />
                    <span className="text-[var(--g66-amber)]">{s.winner_band?.name || '-'}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
